<template>
  <div class="htenant">
    <!-- Mandantenwechsel wie der Organisationswechsel des Mobility Hub (alle angemeldeten Benutzer) -->
    <v-menu
      v-if="canSwitch"
      v-model="open"
      bottom
      offset-y
      :nudge-bottom="6"
      :min-width="280"
      :max-width="400"
      :close-on-content-click="false"
      :role="null"
      content-class="app-hub-tenant-menu"
    >
      <template v-slot:activator="{ on, attrs }">
        <button
          ref="trigger"
          type="button"
          class="htenant__trigger"
          v-bind="attrs"
          aria-haspopup="dialog"
          :aria-label="triggerLabel"
          v-on="on"
        >
          <app-icon name="building" :size="16" class="htenant__icon" />
          <span class="htenant__name" :title="name">{{ name || 'Mandant' }}</span>
          <app-icon name="chevUD" :size="14" class="htenant__chev" />
        </button>
      </template>

      <div ref="panel" class="app-hub-tenant" role="dialog" aria-label="Mandant wechseln" @keydown="onPanelKeydown">
        <div class="app-hub-tenant__search">
          <div class="app-hub-tenant__field">
            <app-icon name="search" :size="14" class="app-hub-tenant__lupe" />
            <input
              ref="search"
              v-model="query"
              type="text"
              class="app-hub-tenant__input"
              placeholder="Kundennummer oder Name"
              aria-label="Mandant suchen"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded="true"
              :aria-controls="state === 'ready' ? listId : null"
              :aria-activedescendant="activeId"
              autocomplete="off"
              autocapitalize="off"
              spellcheck="false"
              @keydown="onSearchKeydown"
            />
          </div>
        </div>

        <div v-if="state === 'idle' || state === 'loading'" class="app-hub-tenant__state">Mandanten werden geladen …</div>
        <div v-else-if="state === 'error'" class="app-hub-tenant__state app-hub-tenant__state--error">
          <span>Die Mandanten konnten nicht geladen werden.</span>
          <app-button kind="tertiary" class="app-hub-tenant__retry" @click="retry">Erneut versuchen</app-button>
        </div>
        <template v-else>
          <ul
            v-show="filtered.length"
            :id="listId"
            ref="list"
            class="app-hub-tenant__list"
            role="listbox"
            aria-label="Mandanten"
          >
            <li
              v-for="(customer, index) in filtered"
              :id="optionId(customer)"
              :key="customer.customer_id"
              class="app-hub-tenant__row"
              :class="{ 'app-hub-tenant__row--active': index === activeIndex }"
              role="option"
              :aria-selected="index === activeIndex ? 'true' : 'false'"
              @mousedown.prevent
              @click="choose(customer)"
            >
              <span class="app-hub-tenant__tile" aria-hidden="true"><app-icon name="building" :size="14" /></span>
              <span class="app-hub-tenant__name" :title="customer.name">{{ customer.name }}</span>
              <span class="app-hub-tenant__id">{{ customer.customer_id }}</span>
              <span
                class="app-hub-tenant__mark"
                :class="{ 'app-hub-tenant__mark--here': isCurrent(customer) }"
                aria-hidden="true"
              >
                <app-icon v-if="isCurrent(customer)" name="check" :size="10" :stroke-width="3" />
              </span>
              <span v-if="isCurrent(customer)" class="app-sr-only">, aktueller Mandant</span>
            </li>
          </ul>
          <!-- ohne Suche nur der aktuelle Mandant mit Hinweis, Treffer erst ab zwei Zeichen oder einer Ziffer -->
          <div
            v-if="hintText"
            class="app-hub-tenant__state"
            :class="{ 'app-hub-tenant__state--hint': filtered.length }"
          >{{ hintText }}</div>
        </template>

        <!-- dauerhafte Statusmeldung für Screenreader: Laden, Fehler, Trefferzahl beim Tippen -->
        <span class="app-sr-only" role="status">{{ statusText }}</span>
      </div>
    </v-menu>

    <!-- alle anderen: nur Anzeige -->
    <span v-else-if="name" class="htenant__static" :title="name">
      <app-icon name="building" :size="16" class="htenant__icon" />
      <span class="app-sr-only">Mandant: </span>
      <span class="htenant__name">{{ name }}</span>
    </span>
  </div>
</template>

<script>
// Mandant in der Hub-Kopfleiste, am Platz des Organisationswechsels im Mobility Hub (Analyse „orgSwitcher“, Farben aus
// tokens.css). Text = customerDisplayName (cust_printname), Gebäude-Icon 16 --ink-3, Name 14/20 in 500 --ink, höchstens
// 280 breit und gekürzt (voller Name im title).
// Angemeldet (tenantSwitchAllowed, alle Benutzer): Knopf 32 hoch (Touch 44), Polster 0 8, Abstand 6, Radius --r-ctl, Doppelpfeil 14
// --ink-3, Hover --hov, gedrückt --press, Fokus 2 px --link mit Abstand 2; offen sieht er aus wie geschlossen (wie im
// Hub). Klick, Enter, Leertaste oder Pfeil ab öffnen das Menü (v-menu, im Portal): linksbündig darunter, Abstand 6,
// Breite 280 bis 400, Radius 10, Kante --line, Schatten --sh-menu, Polster 0. Darin:
//   · Suchzeile (Polster 8 12, Linie unten): Feld 28 hoch (Touch 44 mit 16 px gegen das Zoomen von iOS), 12 px, Lupe 14,
//     „Kundennummer oder Name“; es erhält beim Öffnen den Fokus. Seit 13.10.2026 (Kundenwunsch „auch beim
//     mandantenwechsel bitte mit suchfeld“) ohne Suche nur der aktuelle Mandant und „Kundennummer oder Name eingeben“,
//     Treffer erst ab zwei Zeichen oder einer Ziffer, höchstens acht (src/utils/customerSearch.js, wie die Anmeldung).
//   · Liste (höchstens 300 hoch, scrollt): Zeilen 32 (Touch 48) mit
//     Kachel 24 (Radius 6, Kante --line, Gebäude 14), Name 14 in 500, Nummer 12 --ink-3 und beim aktuellen Mandanten dem
//     orangen Kreis 16 (--acc, „Ort“) mit weißem Haken 10. Sortiert nach Kundennummer (Kundenwunsch 12.10.2026).
//   · Zustände: „Mandanten werden geladen …“, „Die Mandanten konnten nicht geladen werden.“ mit „Erneut versuchen“,
//     „Kein Mandant gefunden“.
// Tastatur (Combobox mit Liste, aria-activedescendant; der Fokus bleibt im Suchfeld): Pfeil ab/auf wählt vor (ohne
// Suche ab dem aktuellen Mandanten, mit Suche ist der erste Treffer vorgewählt), Enter übernimmt, Esc schließt
// (v-menu) und Tab schließt; beide geben den Fokus an den Knopf zurück.
// Auswahl eines anderen Mandanten: Menü zu, $shell.tenantPreselect = { customer_id, name } und $shell.tenantSwitch =
// true; der Dialog AppTenantSwitch (MainLayout) fragt dann das Passwort ab. Der aktuelle Mandant schließt nur das Menü.
// Die Kundenliste kommt einmal je Sitzung von der öffentlichen Schnittstelle customerpublic (wie auf der Anmeldeseite,
// Modulvariable; nach einem Fehler beim nächsten Öffnen oder per „Erneut versuchen“ neu).
// Ohne Profil (beim Abmelden): nur Text (gleiche Schrift, ohne Hover und Doppelpfeil). Offen/zu meldet notePopup.
// Keine Props, Ereignisse oder Slots.
import axios from 'axios';
import { customerDisplayName, tenantSwitchAllowed, byCustomerId } from '../../shell/navigation';
import { notePopup } from '../../shell/shellState';
import { customerQueryReady, searchCustomers } from '@/utils/customerSearch';

// Kundenliste je Sitzung (bis zum Neuladen; ein Mandantenwechsel lädt die App ohnehin neu): [{ customer_id, name }]
let cachedCustomers = null;
let pendingRequest = null;

function loadCustomers(baseUrl) {
  if (cachedCustomers) {
    return Promise.resolve(cachedCustomers);
  }
  if (!pendingRequest) {
    pendingRequest = axios.create().get(baseUrl + 'customerpublic').then((response) => {
      const list = Array.isArray(response.data) ? response.data : [];
      // eingefroren: Vue macht die Einträge nicht reaktiv (lange Listen)
      cachedCustomers = Object.freeze(list
        .filter(c => c && c.customer_id !== null && c.customer_id !== undefined)
        .map(c => Object.freeze({ customer_id: c.customer_id, name: String(c.name || '') }))
        .sort(byCustomerId));
      pendingRequest = null;
      return cachedCustomers;
    }, (error) => {
      pendingRequest = null;
      throw error;
    });
  }
  return pendingRequest;
}

export default {
  name: 'AppHubTenant',
  data() {
    return {
      open: false,
      query: '',
      activeIndex: -1, // vorgewählte Zeile (Tastatur), -1 = keine
      state: cachedCustomers ? 'ready' : 'idle', // 'idle' | 'loading' | 'ready' | 'error'
      customers: cachedCustomers || []
    };
  },
  computed: {
    name() {
      return customerDisplayName(this.$store.state);
    },
    canSwitch() {
      return tenantSwitchAllowed(this.$store.state);
    },
    triggerLabel() {
      return this.name ? this.name + ', Mandant wechseln' : 'Mandant wechseln';
    },
    // als Zahl verglichen (Store: parseInt, Schnittstelle: Zahl)
    currentId() {
      const id = this.$store.state.customerId;
      return id === null || id === undefined || id === '' ? null : Number(id);
    },
    // nicht mehr die ganze Liste (Kundenwunsch 13.10.2026: „auch beim mandantenwechsel bitte mit suchfeld“): ohne Suche
    // nur der aktuelle Mandant, sonst höchstens acht Treffer ab zwei Zeichen oder einer Ziffer (src/utils/customerSearch.js)
    filtered() {
      if (!this.query.trim()) {
        const here = this.customers.find(customer => this.isCurrent(customer));
        return here ? [here] : [];
      }
      return searchCustomers(this.customers, this.query);
    },
    hintText() {
      if (!this.query.trim()) {
        return 'Kundennummer oder Name eingeben';
      }
      if (!customerQueryReady(this.query)) {
        return 'Mindestens 2 Zeichen eingeben';
      }
      return this.filtered.length ? '' : 'Kein Mandant gefunden';
    },
    listId() {
      return 'app-hub-tenant-' + this._uid + '-liste';
    },
    activeId() {
      const customer = this.state === 'ready' ? this.filtered[this.activeIndex] : null;
      return customer ? this.optionId(customer) : null;
    },
    statusText() {
      if (this.state === 'loading') {
        return 'Mandanten werden geladen …';
      }
      if (this.state === 'error') {
        return 'Die Mandanten konnten nicht geladen werden.';
      }
      if (this.state !== 'ready') {
        return '';
      }
      if (!this.query.trim() || !this.filtered.length) {
        return this.hintText;
      }
      const n = this.filtered.length;
      return n === 1 ? '1 Mandant' : n + ' Mandanten';
    }
  },
  watch: {
    open(value) {
      notePopup(value);
      if (!value) {
        return;
      }
      // jedes Öffnen beginnt ohne Suche; der Inhalt ist dabei noch unsichtbar
      this.query = '';
      this.activeIndex = -1;
      if (this.state !== 'ready' && this.state !== 'loading') {
        this.load();
      }
      this.whenVisible(() => {
        this.focusSearch();
        this.revealCurrent();
      });
    },
    // mit Suche ist der erste Treffer vorgewählt (Enter übernimmt ihn), ohne Suche keiner; die Liste beginnt oben
    query() {
      this.activeIndex = this.query.trim() && this.filtered.length ? 0 : -1;
      this.$nextTick(() => {
        if (this.$refs.list) {
          this.$refs.list.scrollTop = 0;
        }
      });
    },
    activeIndex(index) {
      if (index >= 0) {
        this.$nextTick(() => this.reveal(index, false));
      }
    },
    state(value) {
      if (value !== 'ready' || !this.open) {
        return;
      }
      if (this.query.trim()) {
        this.activeIndex = this.filtered.length ? 0 : -1;
      } else {
        this.$nextTick(this.revealCurrent);
      }
    },
    // Recht entfallen (Abmelden): offenes Menü sauber schließen, damit notePopup ausgeglichen bleibt
    canSwitch(value) {
      if (!value) {
        this.open = false;
      }
    }
  },
  beforeDestroy() {
    if (this.open) {
      notePopup(false);
    }
  },
  methods: {
    load() {
      this.state = 'loading';
      return loadCustomers(this.$store.state.baseUrl).then((list) => {
        this.customers = list;
        this.state = 'ready';
      }, () => {
        this.state = 'error';
      });
    },
    // der Knopf verschwindet beim Laden: Fokus vorher ins Suchfeld, sonst ginge er verloren
    retry() {
      this.focusSearch();
      this.load();
    },
    isCurrent(customer) {
      return this.currentId !== null && Number(customer.customer_id) === this.currentId;
    },
    optionId(customer) {
      return this.listId + '-' + customer.customer_id;
    },
    currentIndex() {
      return this.filtered.findIndex(customer => this.isCurrent(customer));
    },
    choose(customer) {
      this.close();
      if (!customer || this.isCurrent(customer)) {
        return;
      }
      // Reihenfolge wichtig: der Dialog liest die Vorauswahl, sobald tenantSwitch wahr wird
      this.$shell.tenantPreselect = { customer_id: customer.customer_id, name: customer.name };
      this.$shell.tenantSwitch = true;
    },
    // schließt und setzt den Fokus auf den Knopf (auch Rücksprung, wenn danach der Dialog zugeht)
    close() {
      this.open = false;
      const trigger = this.$refs.trigger;
      if (trigger) {
        trigger.focus();
      }
    },
    focusSearch() {
      const input = this.$refs.search;
      if (input) {
        input.focus({ preventScroll: true });
      }
    },
    // v-menu blendet den Inhalt erst zwei Frames nach dem Öffnen ein; bis dahin lässt sich nichts fokussieren oder messen
    whenVisible(callback) {
      let frames = 0;
      const step = () => {
        if (!this.open || this._isDestroyed) {
          return;
        }
        const input = this.$refs.search;
        if (input && input.getClientRects().length) {
          callback();
        } else if (frames < 30) {
          frames += 1;
          window.requestAnimationFrame(step);
        }
      };
      this.$nextTick(step);
    },
    // Zeile index im Listenbereich zeigen: center = mittig (aktueller Mandant beim Öffnen), sonst nur so weit wie nötig
    reveal(index, center) {
      const list = this.$refs.list;
      const row = list ? list.children[index] : null;
      if (!row || !list.clientHeight) {
        return;
      }
      const top = row.offsetTop;
      const bottom = top + row.offsetHeight;
      if (center) {
        list.scrollTop = Math.max(0, top - (list.clientHeight - row.offsetHeight) / 2);
      } else if (top < list.scrollTop) {
        list.scrollTop = index === 0 ? 0 : top;
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = index === list.children.length - 1 ? list.scrollHeight : bottom - list.clientHeight;
      }
    },
    revealCurrent() {
      if (this.query.trim()) {
        return;
      }
      const index = this.currentIndex();
      if (index >= 0) {
        this.reveal(index, true);
      }
    },
    // step +1 / -1; ohne Vorwahl beginnt die Tastatur beim aktuellen Mandanten (ohne Suche), sonst am Anfang bzw. Ende
    move(step) {
      const count = this.state === 'ready' ? this.filtered.length : 0;
      if (!count) {
        return;
      }
      if (this.activeIndex < 0 || this.activeIndex >= count) {
        const here = this.query.trim() ? -1 : this.currentIndex();
        this.activeIndex = here >= 0 ? here : (step > 0 ? 0 : count - 1);
      } else {
        this.activeIndex = (this.activeIndex + step + count) % count;
      }
    },
    onSearchKeydown(event) {
      const key = event.key;
      if (key === 'ArrowDown' || key === 'Down') {
        event.preventDefault();
        this.move(1);
      } else if (key === 'ArrowUp' || key === 'Up') {
        event.preventDefault();
        this.move(-1);
      } else if (key === 'Enter' && !event.isComposing) {
        const customer = this.state === 'ready' ? this.filtered[this.activeIndex] : null;
        if (customer) {
          event.preventDefault();
          this.choose(customer);
        }
      }
    },
    // Tab verlässt das Menü nicht ins Leere (der Inhalt hängt am Ende der App): am ersten bzw. letzten bedienbaren
    // Element schließen und zurück zum Knopf; dazwischen (Suchfeld → „Erneut versuchen“) normal weiter
    onPanelKeydown(event) {
      if (event.key !== 'Tab' || !this.$refs.panel) {
        return;
      }
      const focusables = Array.prototype.filter.call(
        this.$refs.panel.querySelectorAll('input, button, [tabindex]'),
        el => el.tabIndex >= 0 && !el.disabled
      );
      const index = focusables.indexOf(document.activeElement);
      const leaving = index === -1 || (event.shiftKey ? index === 0 : index === focusables.length - 1);
      if (leaving) {
        event.preventDefault();
        this.close();
      }
    }
  }
};
</script>

<style scoped>
/* ---------------------------------------------------------------- Knopf bzw. Text in der Kopfleiste */
/* Jede Ebene darf schrumpfen (min-width 0), damit der Name in der Kopfleiste kürzt */
.htenant {
  display: flex;
  align-items: center;
  min-width: 0;
}
.htenant__trigger,
.htenant__static {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  box-sizing: border-box;
  padding: 0 8px;
  color: var(--ink);
  font-family: var(--font);
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
}
.htenant__trigger {
  height: var(--h-ctl);
  margin: 0;
  border: 0;
  border-radius: var(--r-ctl);
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .htenant__trigger:hover {
  background: var(--hov);
}
.htenant__trigger:active {
  background: var(--press);
}
.htenant__trigger:focus {
  outline: none;
}
.htenant__trigger:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
.htenant__static {
  min-height: 32px;
}
.htenant__icon,
.htenant__chev {
  flex: none;
  color: var(--ink-3);
}
.htenant__name {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ---------------------------------------------------------------- Menü (im Portal; Vuetify übernimmt das Scope-Attribut) */
.v-menu__content.app-hub-tenant-menu {
  box-sizing: border-box;
  padding: 0;
  background: var(--surface-raised);
  border: 1px solid var(--line);
  border-radius: 10px;
  box-shadow: var(--sh-menu);
  color: var(--ink);
  font-family: var(--font);
}
@media (prefers-reduced-motion: reduce) {
  .v-menu__content.app-hub-tenant-menu {
    transition: none !important;
  }
}
.app-hub-tenant__search {
  padding: 8px 12px;
  border-bottom: 1px solid var(--line);
}
.app-hub-tenant__field {
  position: relative;
}
.app-hub-tenant__lupe {
  position: absolute;
  top: 50%;
  left: 8px;
  transform: translateY(-50%);
  color: var(--ink-3);
  pointer-events: none;
}
.app-hub-tenant__input {
  display: block;
  width: 100%;
  height: 28px;
  box-sizing: border-box;
  margin: 0;
  padding: 0 8px 0 28px;
  border: 1px solid var(--field-edge);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
  color: var(--ink);
  font-family: var(--font);
  font-size: 12px;
  line-height: 16px;
  outline: none;
  -webkit-appearance: none;
  appearance: none;
}
.app-hub-tenant__input::placeholder {
  color: var(--ink-3);
  opacity: 1;
}
:where(html:not(.dichte-touch)) .app-hub-tenant__input:not(:focus):hover {
  border-color: var(--field-edge-hover);
}
.app-hub-tenant__input:focus-visible {
  outline: 2px solid var(--link);
  outline-offset: 2px;
}
/* Touch: Feld 44, Schrift 16 (iOS zoomt sonst beim Fokus), Lupe 16 */
html.dichte-touch .app-hub-tenant__input {
  height: var(--h-ctl);
  padding-left: 34px;
  font-size: 16px;
  line-height: 20px;
}
html.dichte-touch .app-hub-tenant__lupe {
  left: 10px;
  width: 16px;
  height: 16px;
}

/* Liste: eigener Scrollbereich (Bezug für offsetTop der Zeilen) */
.app-hub-tenant__list {
  position: relative;
  max-height: 300px;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  margin: 0;
  padding: 4px 0;
  list-style: none;
}
.app-hub-tenant__row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: var(--h-menu);
  box-sizing: border-box;
  margin: 0 4px;
  padding: 0 8px;
  border-radius: var(--r-ctl);
  color: var(--ink);
  font-size: 14px;
  line-height: 20px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
:where(html:not(.dichte-touch)) .app-hub-tenant__row:hover {
  background: var(--hov);
}
.app-hub-tenant__row:active {
  background: var(--press);
}
/* vorgewählt (Tastatur) wie die hervorgehobenen Einträge der übrigen Menüs */
.app-hub-tenant__row--active {
  box-shadow: inset 0 0 0 2px var(--link);
}
.app-hub-tenant__tile {
  width: 24px;
  height: 24px;
  flex: none;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--line);
  border-radius: var(--r-ctl);
  background: var(--surface-raised);
}
.app-hub-tenant__name {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: 500;
}
.app-hub-tenant__id {
  flex: none;
  font-size: 12px;
  line-height: 16px;
  font-variant-numeric: tabular-nums;
  color: var(--ink-3);
}
/* Platz für den Kreis in jeder Zeile, damit die Nummern bündig bleiben */
.app-hub-tenant__mark {
  width: 16px;
  height: 16px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
}
.app-hub-tenant__mark--here {
  background: var(--acc);
  color: #fff;
}

/* Zustände */
.app-hub-tenant__state {
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  color: var(--ink-3);
}
/* Hinweis unter dem aktuellen Mandanten: kleiner */
.app-hub-tenant__state--hint {
  padding: 0 12px 10px;
  font-size: 12px;
  line-height: 16px;
}
.app-hub-tenant__state--error {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}
/* Beschriftung des randlosen Knopfs bündig mit dem Text */
.app-hub-tenant__state .app-hub-tenant__retry {
  margin-left: calc(-1 * var(--px-btn));
}
</style>
